import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { AlertCircle } from 'lucide-react';

const AuthCallbackHandler: React.FC = () => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let mounted = true;

    const params = new URLSearchParams(window.location.search);
    const next = params.get('next') || '/';

    const hash = window.location.hash;
    if (hash) {
      const hashParams = new URLSearchParams(hash.substring(1));
      const hashError = hashParams.get('error_description') || hashParams.get('error');
      if (hashError) {
        const email = params.get('email') || '';
        const errorParam = hashError.toLowerCase().includes('expired') ? 'expired' : 'invalid';
        navigate(
          `/setup-password?error=${errorParam}${email ? `&email=${encodeURIComponent(email)}` : ''}`,
          { replace: true }
        );
        return;
      }
    }

    const poll = async () => {
      const maxAttempts = 30;
      for (let i = 0; i < maxAttempts; i++) {
        if (!mounted) return;
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          if (mounted) navigate(next, { replace: true });
          return;
        }
        await new Promise(r => setTimeout(r, 500));
      }
      if (mounted) setErrorMessage('We could not verify your authentication. The link may have expired.');
    };

    poll();

    return () => { mounted = false; };
  }, [navigate]);

  if (errorMessage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <AlertCircle size={48} className="text-amber-500 mx-auto mb-4" />
          <p className="text-gray-900 font-bold text-xl mb-2">Something went wrong</p>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="bg-blue-600 text-white py-2.5 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );
};

export default AuthCallbackHandler;
